import PropTypes from 'prop-types';
import React from 'react';
import { commitMutation, graphql } from 'react-relay';
import { Button, Checkbox, Label, Message } from 'semantic-ui-react';
import { FloatingLabelInput as Input } from '../Elements';
import { GlobalNotifications, Intent } from '../Modules';
import Formsy from 'formsy-react';
import PhoneUtils from '../../lib/utils/phone';
import MaskedInput from 'react-input-mask';

export default class SMSNotifications extends React.Component {
  static propTypes = {
    relay: PropTypes.object.isRequired,
    viewer: PropTypes.object.isRequired
  };

  constructor(props) {
    super(props);
    this.state = {
      phone: '',
      verificationCode: '',
      changeNumber: false,
      initialVerificationMessage: true
    };
    this.onNotificationsChange = this.onNotificationsChange.bind(this);
    this.verifyPhone = this.verifyPhone.bind(this);
    this.changeNumber = this.changeNumber.bind(this);
    this.updatePhone = this.updatePhone.bind(this);
    this.cancelChangeNumber = this.cancelChangeNumber.bind(this);
    this.sendVerificationCode = this.sendVerificationCode.bind(this);
    this.resendVerificationCode = this.resendVerificationCode.bind(this);
    this.textFieldChange = this.textFieldChange.bind(this);
    this.invalidPhone = this.invalidPhone.bind(this);
  }

  onNotificationsChange(e, data) {
    const smsNotifications = data.checked;
    const phone = this.props.viewer.phones.edges[0].node;
    const onError = () => {
      GlobalNotifications.show({
        message: 'Unable to enable SMS notifications',
        intent: Intent.DANGER
      });
    };
    const onCompleted = () => {
      if (!smsNotifications || phone.verified) {
        GlobalNotifications.show({
          message: `Successfully ${smsNotifications
            ? 'enabled'
            : 'disabled'} SMS notifications`,
          intent: Intent.SUCCESS
        });
      } else {
        this.sendVerificationCode(phone);
        GlobalNotifications.show({
          message:
            'Please enter verification code to confirm SMS notifications',
          intent: Intent.WARNING
        });
      }
    };
    const mutation = graphql`
      mutation SMSNotifications_updateUserMutation($input: UpdateUserInput!) {
        updateUser(input: $input) {
          user {
            s_text_on_embed_submit
          }
        }
      }
    `;

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          id: this.props.viewer.id,
          s_text_on_embed_submit: smsNotifications
        }
      },
      onCompleted,
      onError
    });
  }

  changeNumber() {
    this.setState({ changeNumber: true });
  }

  cancelChangeNumber() {
    this.setState({ changeNumber: false, phone: '' });
  }

  invalidPhone() {
    GlobalNotifications.show({
      message: 'Invalid phone number',
      intent: Intent.DANGER
    });
  }

  updatePhone() {
    const onError = () => {
      GlobalNotifications.show({
        message: 'Unable to update phone number',
        intent: Intent.DANGER
      });
    };
    const onCompleted = response => {
      this.setState({ phone: '', changeNumber: false });
      if (response.updatePhone.phone.verified) {
        GlobalNotifications.show({
          message: 'Successfully updated phone number',
          intent: Intent.SUCCESS
        });
      } else {
        this.sendVerificationCode(this.props.viewer.phones.edges[0].node);
        GlobalNotifications.show({
          message:
            'Please enter verification code to confirm SMS notifications',
          intent: Intent.WARNING
        });
      }
    };

    const mutation = graphql`
      mutation SMSNotifications_updatePhoneMutation($input: UpdatePhoneInput!) {
        updatePhone(input: $input) {
          phone {
            id
            number
            verified
          }
        }
      }
    `;

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          id: this.props.viewer.phones.edges[0].node.id,
          number: this.state.phone
        }
      },
      onCompleted,
      onError
    });
  }

  textFieldChange(e) {
    const newValue = {};
    newValue[e.target.name] = e.target.value;
    this.setState(Object.assign(this.state, newValue));
  }

  sendVerificationCode(phone) {
    this.setState({ initialVerificationMessage: false });

    const mutation = graphql`
      mutation SMSNotifications_sendPhoneVerificationCodeMutation(
        $input: SendPhoneVerificationCodeInput!
      ) {
        sendPhoneVerificationCode(input: $input) {
          phone {
            id
            number
            verified
          }
        }
      }
    `;

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: { input: { id: phone.id } }
    });
  }

  resendVerificationCode() {
    this.sendVerificationCode(this.props.viewer.phones.edges[0].node);
    GlobalNotifications.show({
      message: 'Please enter verification code to confirm SMS notifications',
      intent: Intent.WARNING
    });
  }

  verifyPhone() {
    const onError = () => {
      GlobalNotifications.show({
        message: 'Incorrect verification code. Please check for a new code.',
        intent: Intent.DANGER
      });
      this.sendVerificationCode(this.props.viewer.phones.edges[0].node);
    };
    const onCompleted = () => {
      GlobalNotifications.show({
        message: 'Successfully enabled SMS notifications',
        intent: Intent.SUCCESS
      });
    };

    const mutation = graphql`
      mutation SMSNotifications_verifyPhoneMutation($input: VerifyPhoneInput!) {
        verifyPhone(input: $input) {
          phone {
            id
            number
            verified
          }
        }
      }
    `;

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          id: this.props.viewer.phones.edges[0].node.id,
          verification_code: this.state.verificationCode
        }
      },
      onCompleted,
      onError
    });

    this.setState({ verificationCode: '' });
  }

  render() {
    const phone = this.props.viewer.phones.edges[0].node;
    return (
      <div>
        <Formsy.Form className='ui form'>
          <Checkbox
            label='Send SMS Notifications'
            onChange={this.onNotificationsChange}
            name='notifications'
            checked={this.props.viewer.s_text_on_embed_submit}
          />
        </Formsy.Form>

        {this.props.viewer.s_text_on_embed_submit &&
          <div>
            <br />
            {this.state.changeNumber &&
              <Formsy.Form
                className='ui form'
                onValidSubmit={this.updatePhone}
                onInvalidSubmit={this.invalidPhone}
              >
                <Input
                  label='Phone Number'
                  type='tel'
                  name='phone'
                  value={this.state.phone}
                  onChange={this.textFieldChange}
                  validations={{ matchRegexp: /\(\d{3}\)\s\d{3}-\d{4}|\d{10}/ }}
                  validationError='Phone number is invalid'
                  required
                >
                  <MaskedInput mask='(999) 999-9999' />
                </Input>
                <Button type='submit' primary content='Update Phone Number' />
                <Button
                  basic
                  content='Cancel'
                  onClick={this.cancelChangeNumber}
                />
              </Formsy.Form>}
            {!this.state.changeNumber &&
              <div>
                <strong>
                  {PhoneUtils.formatPhoneNumber(phone.number)}
                </strong>
                <Label
                  color={phone.verified ? 'green' : 'red'}
                  horizontal
                  size='small'
                  style={{ marginLeft: '1em' }}
                >
                  {phone.verified ? 'Verified' : 'Unverified'}
                </Label>
                <p>
                  <a onClick={this.changeNumber}>Change Number</a>
                </p>
                {!phone.verified &&
                  <div>
                    {this.state.initialVerificationMessage &&
                      <div style={{ marginTop: '2em' }}>
                        <Message negative>
                          <Message.Header>
                            Phone number not verified
                          </Message.Header>
                          <br />
                          <p>
                            Your phone number has not been verified. You will
                            not receive SMS messages until you verify your
                            number.
                          </p>
                          <Button
                            primary
                            content='Verify Now'
                            onClick={this.resendVerificationCode}
                          />
                        </Message>
                      </div>}
                    {!this.state.initialVerificationMessage &&
                      <Message>
                        <Message.Header>SMS Verification</Message.Header>
                        <p>
                          <br />
                          We just sent a verification code to your device.
                          Please enter it below.
                          <br />
                          You will not receive SMS messages until you verify
                          your number.
                        </p>
                        <Formsy.Form
                          className='ui form'
                          onSubmit={this.verifyPhone}
                        >
                          <Input
                            label='Verification Code'
                            type='text'
                            name='verificationCode'
                            value={this.state.verificationCode}
                            onChange={this.textFieldChange}
                            validations={{
                              isInt: true,
                              isLength: 4
                            }}
                            validationError='Please enter 4-digit verification code'
                            required
                          />
                          <Button type='submit' primary content='Verify' />
                        </Formsy.Form>
                        <p>
                          Didn&apos;t receive a code? &nbsp;
                          <a onClick={this.resendVerificationCode}>Resend</a>
                        </p>
                      </Message>}
                  </div>}
              </div>}
          </div>}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./webpack/components/Leads/SMSNotifications.js